import {
	MatchProposalAssistantExpertReport,
	MatchProposalAssistantGenerateResponse,
	MatchProposalAssistantGetResponse,
	generateMatchProposalAssistant,
	generateOverallAssessment,
	getMatchProposalAssistant,
} from "@src/api/match";
import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";

const useProposalAssistant = (matchUuid?: string) => {
	const queryClient = useQueryClient();

	const queryKey = ["matchProposalAssistant", matchUuid];
	const generatingKey = ["proposalAssistantGenerating", matchUuid];
	const errorKey = ["proposalAssistantError", matchUuid];
	const generatingOverallKey = ["proposalAssistantGeneratingOverall", matchUuid];

	const query = useQuery(
		queryKey,
		async () => {
			if (!matchUuid) return null;
			return await getMatchProposalAssistant(matchUuid);
		},
		{
			enabled: !!matchUuid,
			refetchOnWindowFocus: false,
			staleTime: 0,
			cacheTime: 5 * 60 * 1000,
		},
	);

	// reactive한 생성 상태 (useQuery로 구독)
	const { data: isGenerating = false } = useQuery<boolean>(
		generatingKey,
		() => !!queryClient.getQueryData<boolean>(generatingKey),
		{ enabled: !!matchUuid, staleTime: Infinity, cacheTime: Infinity, refetchOnWindowFocus: false },
	);

	const { data: generationError = null } = useQuery<string | null>(
		errorKey,
		() => queryClient.getQueryData<string | null>(errorKey) || null,
		{ enabled: !!matchUuid, staleTime: Infinity, cacheTime: Infinity, refetchOnWindowFocus: false },
	);

	const generateReport = useCallback(
		async (forceRegenerate = false, matchedExpertDataUuid?: string): Promise<MatchProposalAssistantGenerateResponse | null> => {
			if (!matchUuid) return null;
			if (isGenerating) return null;

			// 생성 중 상태 마킹 → useQuery 구독자들 리렌더링
			queryClient.setQueryData(generatingKey, true);
			queryClient.setQueryData(errorKey, null);

			// fire-and-forget: 컴포넌트 unmount되어도 계속 실행
			generateMatchProposalAssistant({ matchUuid, matchedExpertDataUuid, forceRegenerate })
				.then((result) => {
					if (result?.isSuccess && result.report) {
						queryClient.setQueryData<MatchProposalAssistantGetResponse>(queryKey, {
							isSuccess: true,
							exists: true,
							sourceHash: result.sourceHash,
							promptVersion: result.promptVersion,
							model: result.model,
							responseId: result.responseId,
							generatedAt: result.generatedAt,
							report: result.report,
						});
					} else {
						queryClient.setQueryData(
							errorKey,
							result?.message || result?.errorCode || "리포트 생성에 실패했습니다.",
						);
						queryClient.invalidateQueries(queryKey);
					}
				})
				.catch((error: unknown) => {
					const message = error instanceof Error ? error.message : null;
					queryClient.setQueryData(
						errorKey,
						message || "리포트 생성 중 오류가 발생했습니다.",
					);
				})
				.finally(() => {
					queryClient.setQueryData(generatingKey, false);
				});

			return null;
		},
		[matchUuid, isGenerating, queryClient, generatingKey, errorKey, queryKey],
	);

	const { data: isGeneratingOverall = false } = useQuery<boolean>(
		generatingOverallKey,
		() => !!queryClient.getQueryData<boolean>(generatingOverallKey),
		{ enabled: !!matchUuid, staleTime: Infinity, cacheTime: Infinity, refetchOnWindowFocus: false },
	);

	const generateOverall = useCallback(
		async (forceRegenerate = false): Promise<void> => {
			if (!matchUuid) return;
			if (isGeneratingOverall) return;

			queryClient.setQueryData(generatingOverallKey, true);
			queryClient.setQueryData(errorKey, null);

			generateOverallAssessment({ matchUuid, forceRegenerate })
				.then((result) => {
					if (result?.isSuccess && result.report) {
						queryClient.setQueryData<MatchProposalAssistantGetResponse>(queryKey, {
							isSuccess: true,
							exists: true,
							report: result.report,
						});
					} else {
						queryClient.setQueryData(
							errorKey,
							result?.message || result?.errorCode || "총평 생성에 실패했습니다.",
						);
					}
				})
				.catch((error: unknown) => {
					const message = error instanceof Error ? error.message : null;
					queryClient.setQueryData(errorKey, message || "총평 생성 중 오류가 발생했습니다.");
				})
				.finally(() => {
					queryClient.setQueryData(generatingOverallKey, false);
				});
		},
		[matchUuid, isGeneratingOverall, queryClient, generatingOverallKey, errorKey, queryKey],
	);

	const report = query.data?.report || null;
	const expertReportMap = useMemo(() => {
		const map = new Map<string, MatchProposalAssistantExpertReport>();
		if (report?.experts) {
			report.experts.forEach((expert) => {
				map.set(expert.matchedExpertDataUuid, expert);
			});
		}
		return map;
	}, [report]);

	return {
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isGenerating: !!isGenerating,
		isGeneratingOverall: !!isGeneratingOverall,
		exists: !!query.data?.exists,
		hasDeadlineSet: !!query.data?.hasDeadlineSet,
		report,
		generatedAt: query.data?.generatedAt || null,
		model: query.data?.model || null,
		generationError: generationError || null,
		expertReportMap,
		refetch: query.refetch,
		generateReport,
		generateOverall,
	};
};

export default useProposalAssistant;
